import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useLoadScript } from "@react-google-maps/api";
import TopHeaderFull from "../components/TopHeaderFull";
import Footer from "../components/Footer";
import SixBlocks from "../components/SixBlocks";
import Faqs from "../blocs/Faqs";
import SearchResultCard from "../components/SearchResultCard";
import MapWithResults from "../components/MapComponent";
import CategoryFilterOffcanvas from "../components/CategoryFilter";
import BusinessCard from "../components/BusinessCard";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CustomSelect from "../components/inputs/select";
import { fetchSearchResults, setSortBy } from "../store/slices/searchSlice";
import CategoriesGoogle from "../utils/type.json";
import { Helmet } from "react-helmet-async";

const libraries = ["places"];

const ResultatRecherche = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // Get search state from Redux store
  const searchState = useSelector((state) => state.search);
  const {
    results,
    googleResults,
    loadingSearch,
    categoryName,
    query,
    sortBy,
    filtre,
    categoryId,
    total,
    last_page,
    page,
  } = searchState;

  const { faqs, status } = useSelector((state) => state.blocs);
  const { categories } = useSelector((state) => state.categories);
  const { isAuthenticated } = useSelector((state) => state.user);
  const selectedCategory = useSelector(
    (state) => state.categories.selectedCategory
  );

  // Local state for UI controls that don't need to be in Redux
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [hoveredId, setHoveredId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const cardRefs = useRef({});

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // **FIX 2: Correctly combine results from both sources for rendering.**
  // Removed unnecessary dependencies like 'location' and 'dispatch'.
  const combinedResults = useMemo(() => {
    const localResults = Array.isArray(results) ? results : [];
    const googleResultsArray = Array.isArray(googleResults)
      ? googleResults
      : [];

    // On the last page of our results, append the Google results.
    if (page === last_page || last_page === 0) {
      return [...localResults, ...googleResultsArray];
    }
    return localResults;
  }, [results, googleResults, page, last_page]);

  // **FIX 3: Removed problematic useEffect hooks.**
  // Fetching data should be triggered by user actions (handled by the event handlers below)
  // or when the component initially loads if results are not present.
  // The original useEffects were causing redundant API calls and state conflicts.
  // The initial fetch should ideally be dispatched by the action that navigates to this page.

  useEffect(() => {
    // This effect ensures that if the user lands on this page directly (e.g., via a bookmark)
    // and there are search criteria but no results, a search is triggered.
    const hasSearchCriteria = query || categoryId || filtre?.length > 0;
    if (
      hasSearchCriteria &&
      !loadingSearch &&
      results.length === 0 &&
      googleResults.length === 0
    ) {
      dispatch(
        fetchSearchResults({
          query: query || "",
          location: searchState.location || "",
          lat: searchState.lat || null,
          lng: searchState.lng || null,
          categoryId: categoryId || null,
          filtre: filtre || [],
          sortBy: sortBy,
          cityName: searchState.cityName || null,
          per_page: resultsPerPage,
          page: 1,
        })
      );
    } else {
      dispatch(fetchSearchResults([]));
    }
  }, [location]); // Trigger on initial load or URL change.

  const handleMarkerClick = (id) => {
    const cardElement = cardRefs.current[id];
    if (cardElement) {
      cardElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      cardElement.classList.add("highlight-card");
      setTimeout(() => {
        cardElement.classList.remove("highlight-card");
      }, 2000);
    }
  };

  // **FIX 4: Simplified page change handler.**
  // It now only dispatches the action. The component will update automatically
  // when the 'page' value changes in the Redux store.
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= (last_page || 1)) {
      window.scrollTo(0, 0);
      const searchParams = {
        query: searchState.query || "",
        location: searchState.location || "",
        lat: searchState.lat || null,
        lng: searchState.lng || null,
        categoryId: searchState.categoryId || null,
        filtre: searchState.filtre || [],
        sortBy: sortBy,
        cityName: searchState.cityName || null,
        per_page: resultsPerPage,
        page: newPage, // Pass the new page number
      };
      setCurrentPage(newPage);
      dispatch(fetchSearchResults(searchParams));
    }
  };

  const handleResultsPerPageChange = (event) => {
    const newResultsPerPage = Number(event.target.value);
    setResultsPerPage(newResultsPerPage);
    setCurrentPage(1);
    const searchParams = {
      query: searchState.query || "",
      location: searchState.location || "",
      lat: searchState.lat || null,
      lng: searchState.lng || null,
      categoryId: searchState.categoryId || null,
      filtre: searchState.filtre || [],
      sortBy: sortBy,
      cityName: searchState.cityName || null,
      per_page: newResultsPerPage,
      page: 1, // Reset to the first page when changing items per page
    };
    dispatch(fetchSearchResults(searchParams));
  };

  const handleSortChange = (value) => {
    dispatch(setSortBy(value));
    const searchParams = {
      query: searchState.query || "",
      location: searchState.location || "",
      lat: searchState.lat || null,
      lng: searchState.lng || null,
      categoryId: searchState.categoryId || null,
      filtre: searchState.filtre || [],
      sortBy: value,
      cityName: searchState.cityName || null,
      per_page: resultsPerPage,
      page: currentPage, // Reset to the first page when sorting changes
    };
    dispatch(fetchSearchResults(searchParams));
  };

  const toggleFilter = () => setIsFilterOpen(!isFilterOpen);

  const getPaginationItems = () => {
    if (!total || !last_page || last_page <= 1) return [];

    const pages = [];
    const totalPages = last_page;
    const current = page;

    // Always show first page
    pages.push(1);

    let start = Math.max(2, current - 1);
    let end = Math.min(totalPages - 1, current + 1);

    if (current > 3) {
      pages.push("...");
    }

    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    if (current < totalPages - 2) {
      pages.push("...");
    }

    if (!pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return pages.filter((item, index) => pages.indexOf(item) === index);
  };

  // UI components for pagination buttons
  const PreviousButtonSVG = ({ disabled }) => (
    <svg
      width="32"
      height="33"
      viewBox="0 0 32 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {" "}
      <rect
        width="32"
        height="32"
        rx="5.33333"
        transform="matrix(-1 0 0 1 32 0.5)"
        fill="white"
      />{" "}
      <path
        d="M18 21.5L13.42 16.4992L18 11.5"
        stroke={disabled ? "#898989" : "#2D71E2"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />{" "}
    </svg>
  );
  const NextButtonSVG = ({ disabled }) => (
    <svg
      width="32"
      height="33"
      viewBox="0 0 32 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {" "}
      <rect y="0.5" width="32" height="32" rx="5.33333" fill="white" />{" "}
      <path
        d="M14 21.5L18.58 16.4992L14 11.5"
        stroke={disabled ? "#898989" : "#2D71E2"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />{" "}
    </svg>
  );

  const categoryDisplay = useMemo(() => {
    if (categoryName) {
      return categoryName.split(",")[0].trim();
    }
    if (filtre?.length > 0) {
      const categoryNames = filtre
        .map((id) => {
          const category = categories
            .flatMap((c) => [c, ...(c.children || [])])
            .find((cat) => cat.id.toString() === id.toString());
          return category?.name;
        })
        .filter(Boolean)
        .join(", ");
      return categoryNames ? categoryNames.split(",")[0].trim() : "Recherche";
    }
    if (selectedCategory?.name) {
      return selectedCategory.name;
    }
    return "Recherche";
  }, [categoryName, filtre, selectedCategory, categories]);
  if (loadingSearch) {
    return (
      <div>
        <TopHeaderFull compact={true} />
        <div className="container espace-top">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Skeleton width={100} />
              </li>
              <li className="breadcrumb-item">
                <Skeleton width={150} />
              </li>
            </ol>
          </nav>
          <Skeleton width={300} height={36} />
          <Skeleton width={100} />
          <Skeleton width={200} height={38} className="mt-3 mb-4" />
          <div className="row">
            <div className="col-md-7">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="mb-3 p-3 border rounded">
                  <div className="d-flex">
                    <Skeleton width={100} height={100} className="mr-8" />
                    <div style={{ flexGrow: 1 }}>
                      <Skeleton width="75%" height={20} className="mb-2" />
                      <Skeleton width="50%" height={16} className="mb-2" />
                      <Skeleton width="100%" height={16} className="mb-2" />
                      <Skeleton width="25%" height={16} />
                    </div>
                  </div>
                </div>
              ))}
              <div className="mt-4">
                <Skeleton width={150} height={16} className="mb-2" />
                <div className="d-flex gap-2">
                  <Skeleton width={100} height={38} />
                  <Skeleton width={100} height={16} />
                  <Skeleton width={100} height={38} />
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <Skeleton height={500} />
            </div>
          </div>
          <div className="mt-5">
            <div className="row">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="col-md-4 mb-3">
                  <Skeleton height={200} />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-5">
            <Skeleton width={200} height={30} className="mb-3" />
            {[...Array(3)].map((_, index) => (
              <div key={index} className="mb-3">
                <Skeleton width="75%" height={20} className="mb-2" />
                <Skeleton width="100%" height={16} />
              </div>
            ))}
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  return (
    <div className="resultat-recherche-page">
      <Helmet>
        <title>Page résultat de recherche</title>
        <meta name="description" content="Résultat recherche prestataire" />
        <meta name="keywords" content="architect, Avocat, Prestataire.... " />
        <meta
          property="og:title"
          content="Page résultat de recherche - Twyny"
        />
        <meta
          property="og:description"
          content="Découvrez notre site web et ses fonctionnalités."
        />
      </Helmet>
      <div className="sticky">
        <TopHeaderFull compact={true} />
      </div>
      <div className="container espace-top">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item grey66 body">
              <a href="/">Accueil</a>
            </li>
            <li
              className="breadcrumb-item active grey66 body"
              aria-current="page"
            >
              {categoryDisplay}
            </li>
          </ol>
        </nav>
        <div className="search-header ">
          {categoryDisplay !== "Recherche" && (
            <h3 className="grey500">{categoryDisplay}</h3>
          )}
          <div className="row justify-content-between gap-2 align-items-center mt-16">
            <div className="col-auto">
              <h5 className="grey500">Sélectionnez un professionnel</h5>
            </div>
            <div
              className="col justify-content-end search-info-buttons"
              style={{ display: "flex", gap: "10px" }}
            >
              <button onClick={toggleFilter} className="filterbtn">
                <img src="/images/filter.svg" alt="Filtrer" />
                <p className="mb-0 legend grey500 ">
                  {" "}
                  Filtrer par type de prestation
                </p>
              </button>
              <CustomSelect
                options={[
                  // { label: "Trier par", value: "" },
                  { label: "Tous", value: "relevance" },
                  { label: "Les mieux notés", value: "highest_rated" },
                  { label: "Les plus notés", value: "most_rated" },
                  { label: "Au plus proche", value: "closest" },
                ]}
                placeholder="Trier par"
                selectedValue={sortBy}
                onChange={handleSortChange}
                className="filterbtn"
                icon={
                  <img
                    src="/images/sort-vertical-02.svg"
                    alt="Trier"
                    className="mr-8"
                  />
                }
              />
            </div>
          </div>
          <div className="search-info grey66 legend mt-8">
            <p>{total + googleResults?.length || 0} résultats</p>
          </div>
        </div>

        <div className="row mt-24">
          <div className="col-md-7">
            {results.length > 0 ? (
              <>
                {results.map((result, index) => (
                  <React.Fragment key={result.id || result.google_place_id}>
                    <div
                      ref={(el) =>
                        (cardRefs.current[result.id || result.google_place_id] =
                          el)
                      }
                    >
                      {result.google_place_id ? (
                        <BusinessCard
                          key={result.google_place_id}
                          name={result.nom}
                          isHovered={hoveredId === result.google_place_id}
                          activity={
                            CategoriesGoogle[result.types[0]] ||
                            "Autre activité"
                          }
                          address={result.description}
                          id={result.google_place_id}
                        />
                      ) : (
                        <SearchResultCard
                          result={result}
                          isHovered={hoveredId === result.id}
                          onMouseEnter={() => setHoveredId(result.id)}
                          onMouseLeave={() => setHoveredId(null)}
                          isAuthenticated={isAuthenticated}
                        />
                      )}
                    </div>
                    {index < results.length - 1 && (
                      <hr className="card-separator" />
                    )}
                  </React.Fragment>
                ))}
              </>
            ) : googleResults.length === 0 ? (
              <p className="text-center my-5">Aucun résultat trouvé</p>
            ) : null}
            {/* //rendre google result */}
            {/* {results.length > 0  && page === last_page ? ( */}
            {/* {console.log(currentPage + "===" + last_page)} */}
            {currentPage === last_page ? (
              <>
                {googleResults.map((result, index) => (
                  <React.Fragment key={result.id || result.google_place_id}>
                    <div
                      ref={(el) =>
                        (cardRefs.current[result.id || result.google_place_id] =
                          el)
                      }
                    >
                      {result.google_place_id ? (
                        <BusinessCard
                          key={result.google_place_id}
                          name={result.nom}
                          isHovered={hoveredId === result.google_place_id}
                          activity={
                            CategoriesGoogle[result.types[0]] ||
                            "Autre activité"
                          }
                          address={result.description}
                          id={result.google_place_id}
                        />
                      ) : (
                        <SearchResultCard
                          result={result}
                          isHovered={hoveredId === result.id}
                          onMouseEnter={() => setHoveredId(result.id)}
                          onMouseLeave={() => setHoveredId(null)}
                          isAuthenticated={isAuthenticated}
                        />
                      )}
                    </div>
                    {index < googleResults.length - 1 && (
                      <hr className="card-separator" />
                    )}
                  </React.Fragment>
                ))}
              </>
            ) : (
              ""
            )}

            {combinedResults.length > 0 && (
              <div className="pagination-controls justify-content-between mt-4">
                <div className="results-per-page">
                  <select
                    id="resultsPerPage"
                    className="form-select-option"
                    value={resultsPerPage}
                    onChange={handleResultsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                  </select>
                  <label
                    htmlFor="resultsPerPage"
                    style={{ marginRight: "10px" }}
                  >
                    éléments par page
                  </label>
                </div>

                <div
                  className="pagination-buttons"
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <button
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="btn twyny-cta-secondary"
                    style={{ border: "none", background: "none", padding: 0 }}
                  >
                    <PreviousButtonSVG disabled={currentPage === 1} />
                  </button>
                  {getPaginationItems().map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        style={{ padding: "8px 14px" }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          width: currentPage === page ? "38px" : "auto",
                          height: currentPage === page ? "37px" : "auto",
                          borderRadius: currentPage === page ? "8px" : "0",
                          padding: currentPage === page ? "8px 14px" : "8px",
                          background: currentPage === page ? "#F6F6F6" : "none",
                          border: "none",
                          cursor: "pointer",
                          textAlign: "center",
                        }}
                      >
                        {page}
                      </button>
                    )
                  )}
                  <button
                    disabled={currentPage === last_page}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="btn twyny-cta-secondary"
                    style={{ border: "none", background: "none", padding: 0 }}
                  >
                    <NextButtonSVG disabled={currentPage === last_page} />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="col-md-5 d-lg-block d-md-block">
            <MapWithResults
              data={combinedResults}
              hoveredId={hoveredId}
              setHoveredId={setHoveredId}
              isGoogleLoaded={isLoaded}
              onMarkerClick={handleMarkerClick}
            />
          </div>
        </div>

        <SixBlocks />
        <div className="mb-5">
          <Faqs faqs={faqs} status={status} />
        </div>
      </div>

      {isFilterOpen && (
        <div className="offcanvas-backdrop" onClick={toggleFilter}></div>
      )}
      <CategoryFilterOffcanvas
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        categoryName={query}
        categoryId={categoryId}
      />
      <Footer />
    </div>
  );
};

export default ResultatRecherche;
